import { useMemo } from "react"
import type { PrepareComponent } from "types"

/*
 * Receive Props, transform them in ComponentProps and return
 * Component with ComponentProps
 *
 * onlyRenderIf: only render component if function returns true
 *
 * onlyUpdate: only update component if a prop in array changes
 */
const prepareComponent: PrepareComponent =
  (usePrepareComponent, options) => (Component) => {
    // PrepareComponent is a higher-order component that wraps the original Component
    // and handles the props transformation, rendering, and updating logic.
    const PrepareComponent = (
      props: Parameters<typeof usePrepareComponent>[0]
    ) => {
      // Transform Props into ComponentProps using the usePrepareComponent function
      const newProps = usePrepareComponent(props)

      // Determine which props to use for controlling component updates
      let updateProps: ReadonlyArray<unknown> = [newProps]
      if (options && options.onlyUpdate) {
        // eslint-disable-next-line react/destructuring-assignment
        updateProps = options.onlyUpdate.map((item) => props[item])
      }

      // MemoizeComponent is a memoized version of the Component that only updates when
      // the specified props change, as defined by the onlyUpdate option.
      const MemoizeComponent = useMemo(
        () => <Component {...newProps} />,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        updateProps
      )

      // Only render the component if the onlyRenderIf option is not provided or
      // if it returns true.
      if (options && options.onlyRenderIf && !options.onlyRenderIf(props)) {
        return null
      }

      return MemoizeComponent
    }

    PrepareComponent.displayName = "PrepareComponent"

    return PrepareComponent
  }

export default prepareComponent
